import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import withReactContent from "sweetalert2-react-content";
import CustomMultiSelect from "../../components/CustomMultiSelect";
import { classList } from "../../utils";
import { getSession } from "../../functions";

function Holidays() {
  const [smsTemplate, setSMSTemplate] = useState([]);
  const MySwal = withReactContent(Swal);
  const session = getSession();

  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // ── Edit state (mirrors NoticeBoard pattern) ──────────────────────────────
  const [isEditing, setIsEditing] = useState(false);
  const [editHolidayId, setEditHolidayId] = useState(null);

  const recordsPerPage = 10;

  const defaultFormData = {
    class: [],
    session: session,
    occasion: "",
    fromDate: "",
    toDate: "",
    isStaffHoliday: false,
  };

  const [formData, setFormData] = useState(defaultFormData);

  const allClassOptions = classList.map((item) => ({
    value: item,
    label: item,
  }));

  const withSelectAll = (options) => [
    { value: "*", label: "Select All" },
    ...options,
  ];

  const handleMultiSelectChange = (selected) => {
    const isSelectAll = selected?.some((s) => s.value === "*");
    let newSelected = isSelectAll ? allClassOptions : selected || [];
    setFormData((prev) => ({
      ...prev,
      class: newSelected.map((s) => s.value),
    }));
  };

  const getSelectedOptions = (allOptions, selectedValues) =>
    allOptions.filter((opt) => selectedValues.includes(opt.value));

  // ── Reset form (used on modal close / after submit) ───────────────────────
  const resetForm = () => {
    setFormData(defaultFormData);
    setIsEditing(false);
    setEditHolidayId(null);
  };

  // ── Populate form for editing (mirrors handleEditClick in NoticeBoard) ────
  const handleEditClick = (item) => {
    setFormData({
      class: item.class || [],
      session: item.session || session,
      occasion: item.occasion || "",
      fromDate: item.date || "", // single-date record → fromDate
      toDate: "",
      isStaffHoliday: item.isStaffHoliday || false,
    });
    setIsEditing(true);
    setEditHolidayId(item._id);

    const modal = new window.bootstrap.Modal(
      document.querySelector(".gd-example-modal-lg"),
    );
    modal.show();
  };

  const fetchHolidays = async () => {
    try {
      const response = await axios.get(
        "https://q1xgsyocc2.execute-api.ap-south-1.amazonaws.com/Prod/Holidays",
        { headers: { Authorization: `${localStorage.getItem("token")}` } },
      );
      if (response.data?.holidays) {
        setHolidays([...response.data.holidays].reverse());
      }
    } catch (err) {
      toast.error("Failed to fetch holidays");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHolidays();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const {
      class: cls,
      session,
      occasion,
      fromDate,
      toDate,
      isStaffHoliday,
    } = formData;

    if (!cls.length || !occasion || !fromDate) {
      toast.error("Class, Occasion and From Date are required");
      return;
    }

    const endDate = toDate || fromDate;

    if (new Date(endDate) < new Date(fromDate)) {
      toast.error("To Date cannot be before From Date");
      return;
    }

    try {
      setSubmitting(true);

      if (isEditing) {
        // ── UPDATE ────────────────────────────────────────────────────────
        await axios.post(
          "https://7otfgyv0ke.execute-api.ap-south-1.amazonaws.com/prod/AddDefaultHolidays",
          {
            _id: editHolidayId,
            class: cls,
            session,
            occasion,
            fromDate,
            toDate: endDate,
            isStaffHoliday,
          },
          { headers: { Authorization: `${localStorage.getItem("token")}` } },
        );
        toast.success("Holiday Updated");
      } else {
        // ── INSERT ────────────────────────────────────────────────────────
        await axios.post(
          "https://7otfgyv0ke.execute-api.ap-south-1.amazonaws.com/prod/AddDefaultHolidays",
          {
            class: cls,
            session,
            occasion,
            fromDate,
            toDate: endDate,
            isStaffHoliday,
          },
          { headers: { Authorization: `${localStorage.getItem("token")}` } },
        );
        toast.success("Holidays Added");
      }

      document.querySelector('[data-bs-dismiss="modal"]')?.click();
      resetForm();
      fetchHolidays();
    } catch (err) {
      toast.error("Try Again Later");
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleStaffHolidayToggle = (e) => {
    setFormData((prev) => ({ ...prev, isStaffHoliday: e.target.checked }));
  };

  const containsSearch = (val, searchLower) => {
    if (!val) return false;
    if (typeof val === "string") return val.toLowerCase().includes(searchLower);
    if (Array.isArray(val))
      return val.some((v) => containsSearch(v, searchLower));
    if (typeof val === "object")
      return Object.values(val).some((v) => containsSearch(v, searchLower));
    return false;
  };

  const filteredRecords = holidays.filter((item) => {
    if (!searchTerm.trim()) return true;
    return containsSearch(item, searchTerm.toLowerCase());
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const totalPages = Math.ceil(filteredRecords.length / recordsPerPage);
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredRecords.slice(
    indexOfFirstRecord,
    indexOfLastRecord,
  );

  const nextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };
  const prevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleDelete = async (id) => {
    const result = await MySwal.fire({
      title: "Are you sure?",
      text: "This holiday will be permanently deleted",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, delete it",
    });

    if (!result.isConfirmed) return;

    try {
      await axios.delete(
        `https://q1xgsyocc2.execute-api.ap-south-1.amazonaws.com/Prod/Holidays?id=${id}`,
        { headers: { Authorization: `${localStorage.getItem("token")}` } },
      );
      toast.success("Holiday Deleted");
      setHolidays((prev) => prev.filter((h) => h._id !== id));
    } catch {
      toast.error("Delete failed");
    }
  };

  const handleExportAllSMSTemplatesExcel = () => {
    if (!smsTemplate || smsTemplate.length === 0) {
      toast.error("No SMS Template records to export.");
      return;
    }
    const sheetData = smsTemplate.map((item) => ({
      "Template ID": item.templateId || "",
      "Template Name": item.templateName || "",
      Variables: item.variables || 0,
      Message: item.message
        ? item.message.replace(/\\r\\n|\\n|\\r/g, "\n")
        : "",
      Date: item.createdAt
        ? new Date(item.createdAt).toLocaleDateString("en-GB")
        : "",
    }));
    const worksheet = XLSX.utils.json_to_sheet(sheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "All Templates");
    XLSX.writeFile(
      workbook,
      `SMSTemplates_${new Date().toISOString().slice(0, 10)}.xlsx`,
    );
    toast.success("Report Generated");
  };

  const handleExportSMSTemplatesVariableWiseExcel = () => {
    if (!smsTemplate || smsTemplate.length === 0) {
      toast.error("No SMS Template records to export.");
      return;
    }
    const groupedByVariables = {};
    smsTemplate.forEach((item) => {
      const varCount = item.variables || 0;
      if (!groupedByVariables[varCount]) groupedByVariables[varCount] = [];
      groupedByVariables[varCount].push({
        "Template ID": item.templateId || "",
        "Template Name": item.templateName || "",
        Variables: varCount,
        Message: item.message
          ? item.message.replace(/\\r\\n|\\n|\\r/g, "\n")
          : "",
        Date: item.createdAt
          ? new Date(item.createdAt).toLocaleDateString("en-GB")
          : "",
      });
    });
    const workbook = XLSX.utils.book_new();
    Object.keys(groupedByVariables).forEach((varCount) => {
      const worksheet = XLSX.utils.json_to_sheet(groupedByVariables[varCount]);
      const range = XLSX.utils.decode_range(worksheet["!ref"]);
      worksheet["!autofilter"] = { ref: XLSX.utils.encode_range(range) };
      XLSX.utils.book_append_sheet(workbook, worksheet, `${varCount}`);
    });
    XLSX.writeFile(
      workbook,
      `SMSTemplates_VariablesWise_${new Date().toISOString().slice(0, 10)}.xlsx`,
    );
    toast.success("Report Generated");
  };

  const handleExportSMSTemplates0VariableExcel = () => {
    if (!smsTemplate || smsTemplate.length === 0) {
      toast.error("No SMS Template records to export.");
      return;
    }
    const zeroVarTemplates = smsTemplate.filter(
      (item) => (item.variables || 0) === 0,
    );
    if (zeroVarTemplates.length === 0) {
      toast.error("No SMS Templates with 0 variables found.");
      return;
    }
    const sheetData = zeroVarTemplates.map((item) => ({
      "Template ID": item.templateId || "",
      "Template Name": item.templateName || "",
      Variables: item.variables || 0,
      Message: item.message
        ? item.message.replace(/\\r\\n|\\n|\\r/g, "\n")
        : "",
      Date: item.createdAt
        ? new Date(item.createdAt).toLocaleDateString("en-GB")
        : "",
    }));
    const worksheet = XLSX.utils.json_to_sheet(sheetData);
    const range = XLSX.utils.decode_range(worksheet["!ref"]);
    worksheet["!autofilter"] = { ref: XLSX.utils.encode_range(range) };
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Zero Variables");
    XLSX.writeFile(
      workbook,
      `SMSTemplates_ZeroVariables_${new Date().toISOString().slice(0, 10)}.xlsx`,
    );
    toast.success("Report Generated");
  };

  return (
    <>
      <div id="db-wrapper">
        <main id="page-content">
          {/* ── Modal ───────────────────────────────────────────────────────── */}
          <div
            className="modal fade gd-example-modal-lg"
            tabIndex="-1"
            role="dialog"
            aria-labelledby="holidayModalLabel"
            aria-hidden="true"
          >
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <form onSubmit={handleSubmit}>
                  <div className="modal-header d-flex justify-content-between">
                    {/* Title changes based on mode — same pattern as NoticeBoard */}
                    <h5 className="modal-title fs-4" id="holidayModalLabel">
                      {isEditing ? "Update Holiday" : "Mark Holiday"}
                    </h5>

                    <div className="d-flex align-items-center">
                      <span className="badge bg-success-soft me-3 px-3">
                        Editable
                      </span>
                      <div className="form-check form-switch me-4">
                        <input
                          type="checkbox"
                          className="form-check-input m-0"
                          checked
                          readOnly
                        />
                      </div>
                      <button
                        type="button"
                        className="btn-close"
                        data-bs-dismiss="modal"
                        aria-label="Close"
                        onClick={resetForm}
                      />
                    </div>
                  </div>

                  <div className="modal-body">
                    <div className="row g-2 mb-2">
                      {/* Occasion */}
                      <div className="col-lg-6 col-12">
                        <label className="form-label mb-1 ms-3">
                          Occasion <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control text-dark"
                          name="occasion"
                          value={formData.occasion}
                          onChange={handleChange}
                          placeholder="Enter Occasion (e.g. Diwali)"
                          required
                        />
                      </div>

                      {/* Class */}
                      <div className="col-lg-6 col-12">
                        <label className="form-label mb-1 ms-3">
                          Class <span className="text-danger">*</span>
                        </label>
                        <CustomMultiSelect
                          options={allClassOptions}
                          withSelectAllFn={withSelectAll}
                          value={getSelectedOptions(
                            allClassOptions,
                            formData.class,
                          )}
                          handleMultiSelectChange={handleMultiSelectChange}
                          path={["holiday", "class"]}
                          placeholder="Select Class"
                        />
                      </div>

                      {/* From Date */}
                      <div className="col-lg-6 col-12">
                        <label className="form-label mb-1 ms-3">
                          From Date <span className="text-danger">*</span>
                        </label>
                        <input
                          type="date"
                          className="form-control text-dark"
                          name="fromDate"
                          value={formData.fromDate}
                          onChange={handleChange}
                          required
                        />
                      </div>

                      {/* To Date */}
                      <div className="col-lg-6 col-12">
                        <label className="form-label mb-1 ms-3">To Date</label>
                        <input
                          type="date"
                          className="form-control text-dark"
                          name="toDate"
                          value={formData.toDate}
                          onChange={handleChange}
                          min={formData.fromDate || undefined}
                        />
                      </div>

                      {/* Staff Holiday Toggle */}
                      <div className="col-6 mt-3">
                        <label
                          htmlFor="isStaffHoliday"
                          className="d-flex align-items-center rounded-3 py-1 cursor-pointer"
                        >
                          <div className="form-check form-switch m-0">
                            <input
                              type="checkbox"
                              role="switch"
                              id="isStaffHoliday"
                              className="form-check-input"
                              checked={formData.isStaffHoliday}
                              onChange={handleStaffHolidayToggle}
                            />
                          </div>
                          <span className="d-flex align-items-center ms-2">
                            <span className="d-flex flex-column">
                              <span className="fw-medium text-dark lh-1">
                                Holiday for Staff
                              </span>
                              <span className="fs-8 text-secondary">
                                Applies this holiday to staff as well
                              </span>
                            </span>
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="modal-footer d-flex justify-content-between py-2">
                    <div>
                      <button
                        type="button"
                        className="btn btn-secondary px-3 me-1 rounded-pill"
                        data-bs-dismiss="modal"
                        onClick={resetForm}
                      >
                        <i className="bi bi-x-lg"></i>
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary px-5 rounded-pill fw-normal"
                        disabled={submitting}
                      >
                        {submitting ? (
                          <div className="spinner-border spinner-border-sm" />
                        ) : isEditing ? (
                          "Update"
                        ) : (
                          "Submit"
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>

          {/* ── Page Content ─────────────────────────────────────────────────── */}
          <div className="">
            <div className="container-fluid my-3 px-3">
              <div className="row g-2">
                {/* Table */}
                <div className="col-lg-8">
                  <div className="table-responsive border-0 overflow-y-hidden bg-white rounded-4">
                    <table className="table mb-0 text-nowrap table-centered">
                      <thead className="table-light">
                        <tr>
                          <th className="w-10">No.</th>
                          <th className="lh-1 w-60">
                            Details
                            <i className="bi bi-info-circle ms-2 text-dark"></i>
                            <br />
                            <span className="fs-8 fw-normal text-secondary">
                              Displaying Information as per Record
                            </span>
                          </th>
                          <th className="lh-1 w-30">
                            Action
                            <i className="bi bi-person-gear ms-2 text-dark"></i>
                            <br />
                            <span className="fs-8 fw-normal text-secondary">
                              Role-Specific Action Panel
                            </span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentRecords.map((item, index) => (
                          <tr key={item._id}>
                            <td>{indexOfFirstRecord + index + 1}.</td>

                            <td className="lh-sm py-4 text-wrap">
                              <span className="text-dark">Occasion</span> —{" "}
                              {item.occasion}
                              <br />
                              <span className="text-dark">Date</span> —{" "}
                              {item.date}
                              <br />
                              <span className="text-dark">Session</span> —{" "}
                              {item.session}
                              <br />
                              <span className="text-dark">Class</span> —{" "}
                              {(item.class || []).join(", ")}
                              <br />
                              <span className="text-dark">
                                Holiday for Staff
                              </span>{" "}
                              — {item.isStaffHoliday ? "Yes" : "No"}
                            </td>

                            <td>
                              <div className="d-flex flex-column gap-1 width-fit-content">
                                <span
                                  onClick={() => handleEditClick(item)}
                                  className="badge bg-success-soft px-4 rounded-3 cursor-pointer"
                                >
                                  Update
                                </span>
                                <span
                                  onClick={() => handleDelete(item._id)}
                                  className="badge bg-danger-soft px-4 rounded-3 cursor-pointer"
                                >
                                  Delete
                                </span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Sidebar */}
                <div className="col-lg-4">
                  <div>
                    <div className="mb-3">
                      <div className="d-flex bg-white justify-content-between rounded-4">
                        <form
                          className="d-flex align-items-center"
                          onSubmit={(e) => e.preventDefault()}
                        >
                          <span className="position-absolute ps-3 search-icon">
                            <i className="bi bi-filter-circle-fill ms-1" />
                          </span>
                          <input
                            type="search"
                            className="form-control ps-7 rounded-3 border-0 rounded-4"
                            placeholder="Filter Data"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                        </form>
                        <Link
                          data-bs-toggle="modal"
                          data-bs-target=".gd-example-modal-lg"
                          onClick={resetForm}
                          className="btn btn-warning px-5 fs-6 fw-medium rounded-4 fit-content"
                        >
                          Add Holiday
                        </Link>
                      </div>
                      <div className="mt-2 ms-4">
                        <nav aria-label="breadcrumb">
                          <ol className="breadcrumb pe-3">
                            <li className="breadcrumb-item">
                              <a href="#">Communicate</a>
                            </li>
                            <li
                              className="breadcrumb-item active"
                              aria-current="page"
                            >
                              Holidays
                            </li>
                          </ol>
                        </nav>
                      </div>
                    </div>

                    <div className="p-4 bg-white rounded-4">
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center">
                          {loading ? (
                            <div
                              className="spinner-border text-dark data-loader-spinner"
                              role="status"
                            ></div>
                          ) : (
                            <img src="/assets/images/folder.svg" alt="" />
                          )}
                          <p className="my-0 fw-semibold lh-1 fs-5 text-dark mx-3">
                            Holidays
                            <br />
                            <span className="text-secondary fs-6 fw-medium lh-sm">
                              0.2 MB Size
                            </span>
                          </p>
                        </div>
                        <span className="badge bg-pink rounded-3 px-4 py-2 me-1 mt-1 cursor-pointer">
                          Export
                        </span>
                      </div>

                      <div className="px-4 py-2 mt-3 bg-light rounded-4">
                        <li className="list-group-item px-0">
                          <a
                            className="d-flex align-items-center text-inherit mb-0 justify-content-between"
                            data-bs-toggle="collapse"
                            href="#specifications"
                            role="button"
                            aria-expanded="false"
                            aria-controls="specifications"
                          >
                            <p className="m-0 fw-medium lh-sm text-dark">
                              Download Report
                            </p>
                            <span className="chevron-arrow ms-4">
                              <i className="bi bi-chevron-down fs-4" />
                            </span>
                          </a>
                          <div
                            className="collapse"
                            id="specifications"
                            data-bs-parent="#ecommerceAccordion"
                          >
                            <div className="py-3">
                              <div className="rounded-4 d-flex flex-wrap">
                                <span
                                  onClick={handleExportAllSMSTemplatesExcel}
                                  className="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  All Templates
                                </span>
                                <span
                                  onClick={
                                    handleExportSMSTemplatesVariableWiseExcel
                                  }
                                  className="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  Variable Wise
                                </span>
                                <span
                                  onClick={
                                    handleExportSMSTemplates0VariableExcel
                                  }
                                  className="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  Templates With 0 Variable
                                </span>
                              </div>
                            </div>
                          </div>
                        </li>
                      </div>
                    </div>

                    {/* Pagination */}
                    <div className="my-4">
                      <nav>
                        <ul className="pagination justify-content-center mb-0">
                          <li
                            className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                          >
                            <button className="page-link" onClick={prevPage}>
                              <i className="bi bi-chevron-left"></i>
                            </button>
                          </li>

                          {(() => {
                            const pageNumbers = [];
                            pageNumbers.push(1);
                            if (currentPage > 3)
                              pageNumbers.push("left-ellipsis");
                            if (currentPage !== 1 && currentPage !== totalPages)
                              pageNumbers.push(currentPage);
                            if (currentPage < totalPages - 2)
                              pageNumbers.push("right-ellipsis");
                            if (totalPages > 1) pageNumbers.push(totalPages);

                            return pageNumbers.map((page, index) => {
                              if (
                                page === "left-ellipsis" ||
                                page === "right-ellipsis"
                              ) {
                                return (
                                  <li
                                    key={index}
                                    className="page-item disabled"
                                  >
                                    <span className="page-link">...</span>
                                  </li>
                                );
                              }
                              return (
                                <li
                                  key={index}
                                  className={`page-item ${currentPage === page ? "active" : ""}`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() => setCurrentPage(page)}
                                  >
                                    {page}
                                  </button>
                                </li>
                              );
                            });
                          })()}

                          <li
                            className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
                          >
                            <button className="page-link" onClick={nextPage}>
                              <i className="bi bi-chevron-right"></i>
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}

export default Holidays;
